/*
  Skinstead Assistant (sections/ai-assistant.liquid). Vanilla JS, no dependencies.
  It reads its settings and the store context from the JSON script tags in the section.
  It loads the catalogue from /products.json after the page has loaded (or on first open),
  keeps a compact in memory list and caches it in sessionStorage for ten minutes.
  Before each request it picks the products most relevant to the latest message by tag
  and keyword match and sends them with the transcript, the store context and the
  merchant settings. Prescription products are never sent unless the setting allows it.
  Replies stream as Server Sent Events: text, products, done, error.
  The widget root is moved to the html element: the body is transformed while the
  panel is open and a transformed ancestor would drag a fixed panel with it.
*/
(function () {
  'use strict';
  if (window.SkinsteadAssistant) return;

  var CATALOG_KEY = 'ss_assistant_catalog_v1';
  var CHAT_KEY = 'ss_assistant_chat_v1';
  var CATALOG_TTL = 10 * 60 * 1000;
  var TEASER_KEY = 'ss_assistant_teaser_off';
  var TEASER_DELAY = 3000;
  var TEASER_EVERY = 5000;
  var PAGE_SIZE = 250;
  var MAX_PAGES = 12;
  var MAX_TURNS = 24;
  var RELEVANT = 60;
  var DESCRIPTION_CHARS = 600;
  var TAG_GROUPS = { Concern_: 'concerns', SkinType_: 'skinTypes', HairType_: 'hairTypes', Step_: 'steps', Ingredient_: 'ingredients' };
  var STOP = ['the', 'a', 'an', 'for', 'and', 'or', 'of', 'to', 'with', 'my', 'i', 'is', 'in', 'on', 'me', 'skin', 'product', 'products', 'something', 'good', 'best', 'have', 'need', 'want', 'please', 'can', 'you', 'what', 'which', 'some', 'any', 'this', 'that', 'it', 'am', 'are', 'do', 'does'];
  var SYNONYMS = {
    pimple: 'acne breakouts', pimples: 'acne breakouts', breakout: 'acne', breakouts: 'acne', zits: 'acne',
    oily: 'oiliness', oil: 'oiliness', greasy: 'oiliness', pores: 'oiliness blackheads',
    dry: 'dryness dehydration moisturise', dehydrated: 'dehydration', flaky: 'dryness',
    sun: 'sun protection sunscreen', spf: 'sunscreen sun protection', sunscreen: 'sun protection',
    spots: 'dark spots pigmentation', marks: 'dark spots pigmentation', pigment: 'pigmentation', tan: 'pigmentation sun protection',
    wrinkle: 'wrinkles fine lines ageing', wrinkles: 'fine lines ageing', aging: 'ageing fine lines', ageing: 'fine lines',
    hairfall: 'hair fall', hair: 'hair fall scalp', scalp: 'dandruff dry scalp', dandruff: 'scalp',
    sensitive: 'sensitive reactive', redness: 'sensitive reactive', itchy: 'eczema sensitive',
    cleanser: 'cleanse', wash: 'cleanse cleansers', facewash: 'cleanse cleansers', serum: 'treat serums', moisturizer: 'moisturise moisturisers', moisturiser: 'moisturise moisturisers', cream: 'moisturise', lotion: 'moisturise body',
    baby: 'babies kids pediatric', kids: 'babies pediatric', men: 'men beard', pregnant: 'pregnancy', pregnancy: 'pregnancy'
  };

  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function textEl(tag, cls, t) { var e = el(tag, cls); e.textContent = t; return e; }
  function trim(s) { return String(s == null ? '' : s).replace(/^\s+|\s+$/g, ''); }
  function lines(list) { return (Array.isArray(list) ? list : []).map(trim).filter(Boolean); }
  function money(n, cur) { return cur + Math.round(Number(n) || 0).toLocaleString('en-IN'); }
  function readJson(node) { try { return JSON.parse(node.textContent || '{}'); } catch (e) { return {}; } }
  function plainText(html) {
    if (!html) return '';
    try {
      var doc = new DOMParser().parseFromString(String(html), 'text/html');
      return trim((doc.body && doc.body.textContent) || '').replace(/\s+/g, ' ');
    } catch (e) {
      return trim(String(html).replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ');
    }
  }
  function tokens(s) {
    var out = [];
    String(s || '').toLowerCase().split(/[^a-z0-9+%]+/).forEach(function (t) {
      if (t.length > 1 && STOP.indexOf(t) === -1) {
        out.push(t);
        if (SYNONYMS[t]) SYNONYMS[t].split(' ').forEach(function (x) { out.push(x); });
      }
    });
    return out;
  }

  /* ---------- Catalogue ---------- */

  function compactProduct(p) {
    var variants = Array.isArray(p.variants) ? p.variants : [];
    var v = variants[0] || {};
    var rawTags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    var out = {
      handle: p.handle,
      title: p.title || '',
      vendor: p.vendor || '',
      productType: p.product_type || '',
      tags: [],
      price: parseFloat(v.price) || 0,
      compareAtPrice: null,
      image: p.images && p.images[0] && p.images[0].src ? p.images[0].src : null,
      url: location.origin + '/products/' + p.handle,
      description: plainText(p.body_html).slice(0, DESCRIPTION_CHARS),
      concerns: [], skinTypes: [], hairTypes: [], steps: [], ingredients: [],
      prescriptionOnly: false,
      available: variants.length ? variants.some(function (x) { return !!x.available; }) : true
    };
    var compare = parseFloat(v.compare_at_price) || 0;
    if (compare > out.price) out.compareAtPrice = compare;
    rawTags.map(trim).filter(Boolean).forEach(function (t) {
      var matched = false;
      for (var prefix in TAG_GROUPS) {
        if (t.indexOf(prefix) === 0) { out[TAG_GROUPS[prefix]].push(t.slice(prefix.length)); matched = true; break; }
      }
      if (!matched) {
        if (t === 'Rx') out.prescriptionOnly = true;
        else if (t.indexOf('_') === -1) out.tags.push(t);
      }
    });
    if (out.productType === 'Prescription (Rx)') out.prescriptionOnly = true;
    return out;
  }

  function readCatalogCache() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(CATALOG_KEY) || 'null');
      if (saved && saved.at && Date.now() - saved.at < CATALOG_TTL && Array.isArray(saved.items) && saved.items.length) return saved.items;
    } catch (e) { /* storage unavailable */ }
    return null;
  }
  function writeCatalogCache(items) {
    try { sessionStorage.setItem(CATALOG_KEY, JSON.stringify({ at: Date.now(), items: items })); } catch (e) { /* storage full or blocked */ }
  }

  var catalogPromise = null;
  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    var cached = readCatalogCache();
    if (cached) { catalogPromise = Promise.resolve(cached); return catalogPromise; }
    // Compact products in small batches, yielding between them, so the page never gets a long task.
    function compactAll(items) {
      return new Promise(function (resolve) {
        var out = []; var i = 0;
        var step = function () {
          var end = Math.min(i + 20, items.length);
          for (; i < end; i++) out.push(compactProduct(items[i]));
          if (i < items.length) {
            if ('requestIdleCallback' in window) window.requestIdleCallback(step, { timeout: 500 }); else setTimeout(step, 0);
          } else {
            resolve(out);
          }
        };
        step();
      });
    }
    function page(n, acc) {
      return fetch('/products.json?limit=' + PAGE_SIZE + '&page=' + n, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (json) {
          var items = (json && Array.isArray(json.products)) ? json.products : [];
          return compactAll(items).then(function (compacted) {
            var next = acc.concat(compacted);
            if (items.length === 0 || items.length < PAGE_SIZE || n >= MAX_PAGES) return next;
            return page(n + 1, next);
          });
        });
    }
    catalogPromise = page(1, []).then(function (list) {
      if (list.length) writeCatalogCache(list);
      return list;
    }).catch(function () { catalogPromise = null; return []; });
    return catalogPromise;
  }
  function scheduleCatalog() {
    var start = function () { loadCatalog(); };
    var idle = function () { if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 4000 }); else setTimeout(start, 1500); };
    if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
  }

  /* Pick the products most relevant to the latest message. */
  function selectRelevant(catalog, latest, previous, allowRx, limit) {
    var q = tokens(latest);
    var prev = tokens(previous);
    var pool = catalog.filter(function (p) { return allowRx || !p.prescriptionOnly; });
    var scored = pool.map(function (p) {
      var title = p.title.toLowerCase();
      var brand = (p.vendor + ' ' + p.productType + ' ' + p.tags.join(' ')).toLowerCase();
      var facets = (p.concerns.join(' ') + ' ' + p.steps.join(' ') + ' ' + p.skinTypes.join(' ') + ' ' + p.hairTypes.join(' ')).toLowerCase();
      var ingredients = p.ingredients.join(' ').toLowerCase();
      var desc = p.description.toLowerCase();
      var score = 0;
      var hit = function (t, weight) {
        if (title.indexOf(t) !== -1) score += 4 * weight;
        else if (facets.indexOf(t) !== -1) score += 3 * weight;
        else if (brand.indexOf(t) !== -1) score += 3 * weight;
        else if (ingredients.indexOf(t) !== -1) score += 2 * weight;
        else if (desc.indexOf(t) !== -1) score += 1 * weight;
      };
      q.forEach(function (t) { hit(t, 1); });
      prev.forEach(function (t) { hit(t, 0.4); });
      var phrase = latest.toLowerCase();
      p.concerns.forEach(function (c) { if (c && phrase.indexOf(c.toLowerCase()) !== -1) score += 6; });
      if (p.available) score += 0.3;
      return { p: p, score: score };
    });
    var anyHit = scored.some(function (s) { return s.score >= 1; });
    if (!anyHit) {
      // Nothing specific asked yet: send a spread across routine steps so the assistant can still answer.
      var byStep = {}; var order = [];
      pool.forEach(function (p) { var k = p.steps[0] || p.productType || 'other'; if (!byStep[k]) { byStep[k] = []; order.push(k); } byStep[k].push(p); });
      var spread = []; var i = 0;
      while (spread.length < limit && spread.length < pool.length) {
        var k = order[i % order.length]; var bucket = byStep[k];
        if (bucket.length) spread.push(bucket.shift());
        i++;
        if (i > pool.length * 2) break;
      }
      return spread;
    }
    scored.sort(function (a, b) { return b.score - a.score || a.p.price - b.p.price; });
    return scored.slice(0, limit).map(function (s) { return s.p; });
  }

  /* ---------- Widget ---------- */

  function Widget(root) {
    this.root = root;
    this.o = readJson(root.querySelector('[data-ai-settings]'));
    this.context = readJson(root.querySelector('[data-ai-context]'));
    this.strings = this.o.strings || {};
    this.suggestions = lines(this.o.suggestions);
    this.banned = lines(this.o.bannedPhrases);
    this.launcher = root.querySelector('[data-ai-launcher]');
    this.panel = root.querySelector('.ai-assistant__panel');
    this.log = root.querySelector('[data-ai-log]');
    this.form = root.querySelector('[data-ai-form]');
    this.input = root.querySelector('[data-ai-input]');
    this.sendBtn = root.querySelector('[data-ai-send]');
    this.history = [];
    this.busy = false;
    this.chips = null;
    this.bind();
    this.restore();
    this.teaser();
    scheduleCatalog();
  }

  // Question pop-up above the launcher: rotates the teaser questions until the shopper
  // opens the panel or dismisses it for the session. Tapping a question asks it.
  Widget.prototype.teaser = function () {
    var self = this;
    var box = this.root.querySelector('[data-ai-teaser]');
    var questions = lines(this.o.teasers);
    if (!box || !questions.length) return;
    try { if (sessionStorage.getItem(TEASER_KEY)) return; } catch (e) { /* storage unavailable */ }
    var text = box.querySelector('[data-ai-teaser-text]');
    var i = 0; var timer = null; var paused = false;
    function show() {
      text.textContent = questions[i % questions.length];
      box.classList.remove('is-in'); void box.offsetWidth; box.classList.add('is-in');
    }
    function tick() { if (!paused && !self.root.classList.contains('is-open')) { i++; show(); } }
    box.addEventListener('mouseenter', function () { paused = true; });
    box.addEventListener('mouseleave', function () { paused = false; });
    box.querySelector('[data-ai-teaser-q]').addEventListener('click', function () {
      var q = questions[i % questions.length];
      self.open(); self.submit(q);
    });
    box.querySelector('[data-ai-teaser-close]').addEventListener('click', function () {
      clearInterval(timer); box.hidden = true;
      try { sessionStorage.setItem(TEASER_KEY, '1'); } catch (e) { /* storage unavailable */ }
    });
    setTimeout(function () {
      box.hidden = false; show();
      timer = setInterval(tick, TEASER_EVERY);
    }, TEASER_DELAY);
  };

  Widget.prototype.bind = function () {
    var self = this;
    // Live on <html> so the body transform never moves the fixed panel.
    document.documentElement.appendChild(this.root);
    this.launcher.addEventListener('click', function () { self.open(); });
    this.root.querySelector('[data-ai-close]').addEventListener('click', function () { self.close(); });
    this.root.querySelector('[data-ai-reset]').addEventListener('click', function () { self.reset(); });
    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); self.submit(self.input.value); }
    });
    this.input.addEventListener('input', function () {
      self.input.style.height = 'auto';
      self.input.style.height = Math.min(self.input.scrollHeight, 120) + 'px';
    });
    this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(self.input.value); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.root.classList.contains('is-open')) self.close(); });
    window.addEventListener('resize', function () { if (self.root.classList.contains('is-open')) self.applyLayout(); });
  };

  Widget.prototype.applyLayout = function () {
    var html = document.documentElement;
    var w = Math.min(Number(this.o.panelWidth) || 440, window.innerWidth);
    html.style.setProperty('--ai-assistant-w', w + 'px');
    var scale = window.innerWidth > 767 ? (window.innerWidth - w) / window.innerWidth : 1;
    html.style.setProperty('--ai-assistant-scale', scale.toFixed(4));
  };
  Widget.prototype.open = function () {
    var html = document.documentElement;
    this.applyLayout();
    html.classList.remove('ai-assistant-closing');
    html.classList.add('ai-assistant-open', 'ai-assistant-effect-' + (this.o.effect === 'scale' ? 'scale' : 'push'));
    this.root.classList.add('is-open');
    this.panel.setAttribute('aria-hidden', 'false');
    this.launcher.setAttribute('aria-expanded', 'true');
    loadCatalog();
    this.input.focus();
    this.scroll();
  };
  Widget.prototype.close = function () {
    var html = document.documentElement;
    html.classList.add('ai-assistant-closing');
    html.classList.remove('ai-assistant-open', 'ai-assistant-effect-push', 'ai-assistant-effect-scale');
    setTimeout(function () { html.classList.remove('ai-assistant-closing'); }, 400);
    this.root.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.launcher.setAttribute('aria-expanded', 'false');
    this.launcher.focus();
  };
  Widget.prototype.scroll = function () { this.log.scrollTop = this.log.scrollHeight; };

  Widget.prototype.greet = function () {
    var self = this;
    if (this.o.greeting) this.addBubble('bot', this.o.greeting);
    if (!this.suggestions.length) return;
    var chips = el('div', 'ai-assistant__chips');
    this.suggestions.forEach(function (s) {
      var c = textEl('button', 'ai-assistant__chip', s); c.type = 'button';
      c.addEventListener('click', function () { self.submit(s); });
      chips.appendChild(c);
    });
    this.chips = chips;
    this.log.appendChild(chips);
  };

  Widget.prototype.addBubble = function (role, content, modifier) {
    var cls = 'ai-assistant__msg ai-assistant__msg--' + role + (modifier ? ' ai-assistant__msg--' + modifier : '');
    var b = textEl('div', cls, content || '');
    this.log.appendChild(b); this.scroll(); return b;
  };

  Widget.prototype.addCards = function (items) {
    var grid = el('div', 'ai-assistant__cards'); var cur = this.o.currency || '₹'; var strings = this.strings;
    items.forEach(function (p) {
      var a = el('a', 'ai-assistant__card'); a.href = p.url; a.setAttribute('data-track', 'ai_assistant:product');
      var media = el('div', 'ai-assistant__card-media');
      if (p.image) { var img = document.createElement('img'); img.src = p.image; img.alt = p.title; img.loading = 'lazy'; img.width = 300; img.height = 300; media.appendChild(img); }
      a.appendChild(media);
      var body = el('div', 'ai-assistant__card-body');
      if (p.step) body.appendChild(textEl('div', 'ai-assistant__card-step', p.step));
      body.appendChild(textEl('div', 'ai-assistant__card-name', p.title));
      if (p.brand) body.appendChild(textEl('div', 'ai-assistant__card-brand', p.brand));
      if (p.price > 0) {
        var price = textEl('div', 'ai-assistant__card-price', money(p.price, cur));
        if (p.compareAtPrice && p.compareAtPrice > p.price) { var s = textEl('s', '', money(p.compareAtPrice, cur)); price.appendChild(s); }
        body.appendChild(price);
      }
      if (p.prescriptionOnly) body.appendChild(textEl('span', 'ai-assistant__card-rx', strings.prescriptionOnly || 'Prescription only'));
      if (p.reason) body.appendChild(textEl('div', 'ai-assistant__card-why', p.reason));
      body.appendChild(textEl('span', 'ai-assistant__card-link', strings.viewProduct || 'View product'));
      a.appendChild(body);
      grid.appendChild(a);
    });
    this.log.appendChild(grid); this.scroll();
    return grid;
  };

  Widget.prototype.submit = function (value) {
    var msg = trim(value);
    if (!msg || this.busy) return;
    if (this.chips) { this.chips.remove(); this.chips = null; }
    this.input.value = ''; this.input.style.height = 'auto';
    this.addBubble('user', msg);
    this.history.push({ role: 'user', content: msg });
    this.persist();
    this.stream();
  };

  Widget.prototype.requestBody = function (catalog) {
    var users = this.history.filter(function (m) { return m.role === 'user'; });
    var latest = users.length ? users[users.length - 1].content : '';
    var previous = users.length > 1 ? users[users.length - 2].content : '';
    var allowRx = !!this.o.allowPrescription;
    return {
      messages: this.history.slice(-MAX_TURNS),
      catalog: selectRelevant(catalog, latest, previous, allowRx, RELEVANT),
      context: this.context,
      settings: {
        assistantName: this.o.assistantName,
        tone: this.o.tone,
        bannedPhrases: this.banned,
        allowPrescription: allowRx,
        maxProducts: Number(this.o.maxProducts) || 4
      }
    };
  };

  Widget.prototype.stream = function () {
    var self = this; self.busy = true; self.sendBtn.disabled = true;
    var bubble = self.addBubble('bot', '', 'typing');
    var assistantText = '';
    var cardsShown = [];
    var waiting = setTimeout(function () { if (bubble.textContent === '') bubble.textContent = self.strings.loadingCatalogue || ''; }, 1200);

    loadCatalog().then(function (catalog) {
      clearTimeout(waiting);
      if (bubble.textContent === (self.strings.loadingCatalogue || '')) bubble.textContent = '';
      return fetch(self.o.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(self.requestBody(catalog))
      });
    }).then(function (res) {
      if (!res.ok || !res.body) throw new Error('HTTP ' + res.status);
      var reader = res.body.getReader(); var dec = new TextDecoder(); var buf = '';
      function handle(evt) {
        if (evt.type === 'text') {
          assistantText += evt.text; bubble.textContent = assistantText; self.scroll();
        } else if (evt.type === 'products') {
          bubble.classList.remove('ai-assistant__msg--typing');
          if (bubble.textContent === '') bubble.remove();
          self.addCards(evt.items || []);
          cardsShown = cardsShown.concat((evt.items || []).map(function (p) { return p.title; }));
          bubble = self.addBubble('bot', '', 'typing'); assistantText = '';
        } else if (evt.type === 'error') {
          bubble.classList.remove('ai-assistant__msg--typing'); bubble.classList.add('ai-assistant__msg--error'); bubble.textContent = evt.message;
        }
      }
      function pump() {
        return reader.read().then(function (r) {
          if (r.done) return;
          buf += dec.decode(r.value, { stream: true });
          var parts = buf.split('\n\n'); buf = parts.pop();
          parts.forEach(function (p) {
            var line = p.split('\n').filter(function (l) { return l.indexOf('data:') === 0; }).map(function (l) { return l.slice(5).trim(); }).join('');
            if (!line) return;
            try { handle(JSON.parse(line)); } catch (e) { /* ignore a malformed frame */ }
          });
          return pump();
        });
      }
      return pump();
    }).catch(function () {
      clearTimeout(waiting);
      bubble.classList.add('ai-assistant__msg--error'); bubble.textContent = self.strings.unreachable || 'The assistant is unavailable right now.';
    }).then(function () {
      bubble.classList.remove('ai-assistant__msg--typing');
      if (bubble.textContent === '') bubble.remove();
      // Keep the transcript meaningful for the next turn even when text was split around cards.
      var bots = Array.prototype.slice.call(self.log.querySelectorAll('.ai-assistant__msg--bot:not(.ai-assistant__msg--error)'));
      var recent = bots.slice(-2).map(function (b) { return b.textContent; }).join(' ');
      var full = recent || assistantText;
      if (cardsShown.length) full += ' [Products shown: ' + cardsShown.join('; ') + ']';
      if (trim(full)) self.history.push({ role: 'assistant', content: trim(full) });
      self.persist();
      self.busy = false; self.sendBtn.disabled = false; self.input.focus();
    });
  };

  Widget.prototype.persist = function () {
    try { sessionStorage.setItem(CHAT_KEY, JSON.stringify({ history: this.history, html: this.log.innerHTML })); } catch (e) { /* storage unavailable */ }
  };
  Widget.prototype.restore = function () {
    var saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(CHAT_KEY) || 'null'); } catch (e) { /* storage unavailable */ }
    if (saved && saved.history && saved.history.length) {
      var self = this;
      this.history = saved.history; this.log.innerHTML = saved.html || '';
      Array.prototype.forEach.call(this.log.querySelectorAll('.ai-assistant__chip'), function (c) { c.addEventListener('click', function () { self.submit(c.textContent); }); });
      var chips = this.log.querySelector('.ai-assistant__chips'); if (chips) this.chips = chips;
    } else {
      this.greet();
    }
  };
  Widget.prototype.reset = function () {
    this.history = []; this.log.innerHTML = ''; this.chips = null;
    try { sessionStorage.removeItem(CHAT_KEY); } catch (e) { /* storage unavailable */ }
    this.greet(); this.input.focus();
  };

  var api = {
    instance: null,
    open: function () { if (api.instance) api.instance.open(); },
    // Open the panel and send a question, used by the Skinstead AI feature section.
    ask: function (question) {
      if (!api.instance) return;
      api.instance.open();
      if (trim(question)) api.instance.submit(question);
    }
  };
  window.SkinsteadAssistant = api;

  function boot() {
    if (api.instance) return;
    var root = document.querySelector('[data-ai-assistant]');
    if (!root) return;
    api.instance = new Widget(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
